import React, { createContext, useEffect, useRef } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { useMetricsContext } from '../context';
import { getPerformanceMark } from '../hooks/usePerformanceAnalytics';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';

export type ResizeEvent = {
	elapsed: number;
	height: number;
	type: 'resized';
};

export type ProductRequestsEvent = {
	elapsed: number;
	fetchDuration: number;
	type: 'productFetchRequest';
};

export type PerformanceEvents = ResizeEvent | ProductRequestsEvent;

export type PerformanceAnalyticsValues = {
	eventsRef: React.MutableRefObject<Array<ResizeEvent | ProductRequestsEvent>>;
};

export const PerformanceAnalyticsContext = createContext<PerformanceAnalyticsValues>({
	eventsRef: { current: [] },
});

export const PERFORMANCE_ANALYTICS_INTERVAL = 10000;

// Shorten event keys to reduce payload size
const formatEvents = (
	events: PerformanceAnalyticsValues['eventsRef']['current'],
	elapsedBaselineMark: number,
) => {
	return events.map((event) => {
		const evaluatedElapsedTime = event.elapsed - elapsedBaselineMark;
		if (event.type === 'productFetchRequest') {
			return {
				t: 'productFetchRequest',
				fD: event.fetchDuration,
				e: evaluatedElapsedTime,
			};
		} else if (event.type === 'resized') {
			return {
				t: 'resized',
				h: event.height,
				e: evaluatedElapsedTime,
			};
		}
		return event;
	});
};

export const PerformanceAnalyticsProvider = ({
	children,
	appType,
	forgeEnvironment,
	profileKey,
}: {
	appType: 'UIKit' | 'CustomUI';
	children: React.ReactNode;
	forgeEnvironment: ForgeUIExtensionType['environmentType'];
	profileKey: string;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	// Rely on refs to avoid re-rendering all children components when the state changes
	const eventsRef = useRef<PerformanceAnalyticsValues['eventsRef']['current']>([]);
	const hasSent = useRef(false);
	const elapsedBaselineMark = useRef(0);

	useEffect(() => {
		eventsRef.current = [];
		hasSent.current = false;
		elapsedBaselineMark.current = getPerformanceMark();
		// Use `profileKey` to reset the all state when the extension or entryPoint changes to collect a new set of analytics
	}, [profileKey]);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		// Send all collected performance related analytics in a single event
		const timeout = setTimeout(() => {
			// Clone the eventRef to avoid the possibility of mutating the original object
			const eventsPayload = structuredClone(eventsRef.current);

			fireEvent(createAnalyticsEvent, {
				action: 'performance.profiled',
				actionSubject: 'forge.ui.extension',
				attributes: {
					events: formatEvents(eventsPayload, elapsedBaselineMark.current),
					// Time difference since this event was fired and since page loaded
					navigationTimeStart: elapsedBaselineMark.current,
					appType,
					forgeEnvironment,
				},
				source,
			});

			hasSent.current = true;
		}, PERFORMANCE_ANALYTICS_INTERVAL);

		return () => {
			clearTimeout(timeout);
		};
	}, [
		appType,
		createAnalyticsEvent,
		elapsedBaselineMark,
		eventsRef,
		forgeEnvironment,
		hasSent,
		source,
	]);

	return (
		<PerformanceAnalyticsContext.Provider value={{ eventsRef }}>
			{children}
		</PerformanceAnalyticsContext.Provider>
	);
};
